
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useProject } from "@/hooks/useProject";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { EditTemplateDialog } from "./components/EditTemplateDialog";
import { VariableManager } from "./components/VariableManager";
import { VariableMappingManager } from "./components/VariableMappingManager";
import { CalculationManager } from "./components/CalculationManager";
import { useTemplateVariables } from "@/hooks/useTemplateVariables";
import { FileText, ArrowLeft, Edit, FileQuestion, Eye, Link2, BarChart3, Calculator, Image } from "lucide-react";
import { TemplatePreview } from "./components/TemplatePreview";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChartManager } from "./components/ChartManager";
import { ImageManager } from "./components/ImageManager";

export default function TemplateDetail() {
  const { id } = useParams();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { projectClient, currentProject } = useProject();

  const { data: template, isLoading } = useQuery({
    queryKey: ['template-v2', id],
    queryFn: async () => {
      if (!id) return null;
      
      // Primero obtenemos la plantilla
      const { data: templateData, error: templateError } = await projectClient
        .from('report_templates_v2')
        .select('*')
        .eq('id', id)
        .single();

      if (templateError) throw templateError;
      if (!templateData) return null;

      // Si la plantilla tiene jotform_form_id, obtenemos información del formulario principal
      let formMapping = null;
      if (templateData.jotform_form_id) {
        const { data: formData, error: formError } = await projectClient
          .from('jotform_form_mappings')
          .select(`
            *,
            parent:parent_form_id(
              id,
              form_title,
              jotform_form_id
            )
          `)
          .eq('jotform_form_id', templateData.jotform_form_id)
          .single();

        if (!formError && formData) {
          formMapping = formData;
        }
      }

      // Si la plantilla tiene jotform_form_id_referral, obtenemos información del formulario de referidos
      let formMappingReferral = null;
      if (templateData.jotform_form_id_referral) {
        const { data: formData, error: formError } = await projectClient
          .from('jotform_form_mappings')
          .select(`
            *,
            parent:parent_form_id(
              id,
              form_title,
              jotform_form_id
            )
          `)
          .eq('jotform_form_id', templateData.jotform_form_id_referral)
          .single();

        if (!formError && formData) {
          formMappingReferral = formData;
        }
      }

      return {
        ...templateData,
        jotform_form_mapping: formMapping,
        jotform_form_mapping_referral: formMappingReferral
      };
    },
    enabled: !!id,
  });

  // Get extracted variables
  const { data: extractedVariables = [] } = useTemplateVariables(template?.id || null);

  if (isLoading) {
    return (
      <div className="mx-auto p-6 flex flex-col gap-6">
        <Skeleton className="h-4 w-32" />
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-8 w-8" />
          </div>
          <div className="flex flex-col items-end gap-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-5 w-48" />
          </div>
        </div>
        <div className="mt-6">
          <Skeleton className="h-10 w-full mb-6" />
          <Card>
            <CardContent className="p-6 space-y-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-8 w-24" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  if (!template) {
    return <div className="p-6">No se encontró la plantilla.</div>;
  }

  return (
    <div className="mx-auto p-6 flex flex-col gap-6">
      <div>
        <Link
          to="/admin/templates"
          className="inline-flex items-center text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Volver a la lista
        </Link>
      </div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-2 text-2xl font-bold text-muted-foreground">
            <FileText className="h-6 w-6" />
            {template.name || "Sin nombre"}
          </span>
          <Button size="icon" variant="ghost" onClick={() => setEditDialogOpen(true)} aria-label="Editar plantilla">
            <Edit className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="text-sm text-muted-foreground">
            ID: {template.id}
          </div>
          {template.jotform_form_mapping && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Formulario Principal:</span>
              <Badge variant="secondary" className="text-xs">
                {template.jotform_form_mapping.form_title || template.jotform_form_mapping.jotform_form_id}
              </Badge>
            </div>
          )}
          {template.jotform_form_mapping_referral && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Formulario Referidos:</span>
              <Badge variant="outline" className="text-xs">
                {template.jotform_form_mapping_referral.form_title || template.jotform_form_mapping_referral.jotform_form_id}
              </Badge>
            </div>
          )}
        </div>
      </div>
      <Tabs defaultValue="variables" className="w-full mt-6">
        <TabsList className="w-full grid grid-cols-6 h-auto">
          <TabsTrigger value="variables" className="flex items-center justify-center gap-2 py-3">
            <FileQuestion className="h-4 w-4" /> 
            <span className="hidden sm:inline">Variables Plantilla</span>
            <span className="sm:hidden">Variables</span>
          </TabsTrigger>
          <TabsTrigger value="calculations" className="flex items-center justify-center gap-2 py-3">
            <Calculator className="h-4 w-4" /> 
            <span className="hidden sm:inline">Cálculos</span>
            <span className="sm:hidden">Cálculos</span>
          </TabsTrigger>
          <TabsTrigger value="mapping" className="flex items-center justify-center gap-2 py-3">
            <Link2 className="h-4 w-4" /> 
            <span className="hidden sm:inline">Mapeo de Variables</span>
            <span className="sm:hidden">Mapeo</span>
          </TabsTrigger>
          <TabsTrigger value="charts" className="flex items-center justify-center gap-2 py-3">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Gráficos</span>
            <span className="sm:hidden">Gráficos</span>
          </TabsTrigger>
          <TabsTrigger value="images" className="flex items-center justify-center gap-2 py-3">
            <Image className="h-4 w-4" />
            <span className="hidden sm:inline">Imágenes</span>
            <span className="sm:hidden">Imágenes</span>
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex items-center justify-center gap-2 py-3">
            <Eye className="h-4 w-4" />
            <span className="hidden sm:inline">Vista previa</span>
            <span className="sm:hidden">Preview</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="variables" className="mt-6">
          <VariableManager templateId={template.id} pptFilePath={template.ppt_file_path} />
        </TabsContent>

        <TabsContent value="calculations" className="mt-6">
          <CalculationManager
            templateId={template.id}
            jotformFormId={template.jotform_form_id}
            jotformFormIdReferral={template.jotform_form_id_referral}
          />
        </TabsContent>

        <TabsContent value="mapping" className="mt-6">
          <VariableMappingManager
            templateId={template.id}
            extractedVariables={extractedVariables}
            jotformFormId={template.jotform_form_id}
            jotformFormIdReferral={template.jotform_form_id_referral}
          />
        </TabsContent>

        <TabsContent value="charts" className="mt-6">
          <ChartManager
            templateId={template.id}
            jotformFormId={template.jotform_form_id}
            jotformFormIdReferral={template.jotform_form_id_referral}
          />
        </TabsContent>

        <TabsContent value="images" className="mt-6">
          <ImageManager templateId={template.id} />
        </TabsContent>

        <TabsContent value="preview" className="mt-6">
          <TemplatePreview
            templateId={template.id}
            jotformFormId={template.jotform_form_id}
            jotformFormIdReferral={template.jotform_form_id_referral}
            pptFilePath={template.ppt_file_path}
            canvaTemplateUrl={template.canva_template_url}
          />
        </TabsContent>
      </Tabs>

      <EditTemplateDialog
        template={template}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
      />
    </div>
  );
}
